import { SquareIconMenuButton } from "@/components/menu/buttons";
import { transformers } from "@/configs/workspace";
import { useActionHandler, useHasDataInput, useTranslation } from "@/hooks";
import {
  useActiveDataflow,
  useDataflowActions,
  useDataflowStore,
} from "@/stores/dataflow";
import type {
  DataInputTransformer,
  Transformer,
  UserFileTransformer,
} from "@/types/workspace";
import {
  closestCenter,
  DndContext,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { Button, Card, Flex, Grid, Group, Menu, Text } from "@mantine/core";
import { useForm } from "@mantine/form";
import { IconWashDrycleanOff } from "@tabler/icons-react";
import { useMemo } from "react";
import {
  WorkspaceSidebarFlowInput,
  WorkspaceSidebarFlowTransformer,
} from "../";
import "./WorkspaceSidebarFlow.css";

const isInputType = (type: string) =>
  type === "data_input" || type === "user_file";

const isTransformerType = (type: string) =>
  !isInputType(type) && type !== "data_output";

export function WorkspaceSidebarFlow() {
  const { t } = useTranslation();
  const dataflowData = useActiveDataflow();
  const dataflowId = useDataflowStore((state) => state.activeDataflowId);
  const { reorderTransformers, removeTransformer, updateTransformer } =
    useDataflowActions();
  const actionHandler = useActionHandler();
  const hasDataInput = useHasDataInput();

  const currentFlow = dataflowData?.flow_definition_json || [];

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const form = useForm({
    initialValues: {
      dataflow_id: dataflowData?.dataflow_id,
      flow_definition_json: dataflowData?.flow_definition_json,
      project_id: dataflowData?.project_id,
    },
  });

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (
      !over ||
      active.id === over.id ||
      !dataflowData?.flow_definition_json ||
      !dataflowId
    ) {
      return;
    }

    // Find indices in sortableItems array
    const oldIndex = sortableItems.indexOf(String(active.id));
    const newIndex = sortableItems.indexOf(String(over.id));

    if (oldIndex === -1 || newIndex === -1) {
      return;
    }

    // Get actual indices in the full array
    const oldActualIndex = transformerItems[oldIndex]?.actualIndex;
    const newActualIndex = transformerItems[newIndex]?.actualIndex;

    if (
      oldActualIndex === undefined ||
      newActualIndex === undefined ||
      oldActualIndex === newActualIndex
    ) {
      return;
    }

    // Reorder in the full array
    const newTransformers = arrayMove(
      dataflowData.flow_definition_json,
      oldActualIndex,
      newActualIndex
    );
    reorderTransformers(dataflowId, newTransformers);
  };

  const handleSubmit = (_values: {
    dataflow_id: string | undefined;
    flow_definition_json: Transformer[] | undefined;
    project_id: string | undefined;
  }) => {};

  const dataflowInput = useMemo(
    () =>
      currentFlow.find((item) => isInputType(item.type)) as
        | DataInputTransformer
        | UserFileTransformer
        | undefined,
    [currentFlow]
  );

  const transformerItems = useMemo(
    () =>
      currentFlow
        .map((transformer, actualIndex) => ({
          transformer,
          actualIndex,
        }))
        .filter(({ transformer }) => isTransformerType(transformer.type)),
    [currentFlow]
  );

  const sortableItems = useMemo(
    () =>
      transformerItems.map(
        ({ transformer, actualIndex }) => `${transformer.type}-${actualIndex}`
      ),
    [transformerItems]
  );

  const AddTransformerMenuButton = () => (
    <Menu
      shadow="lg"
      withArrow
      offset={0}
      position="bottom"
      closeOnItemClick={true}
      disabled={!hasDataInput}
      width={250}
    >
      <Menu.Target>
        <Button fullWidth size="xs" variant="light" disabled={!hasDataInput}>
          {t("workspace").addTransformer}
        </Button>
      </Menu.Target>
      <Menu.Dropdown p={4}>
        <Menu.Label ta="center" tt="uppercase" fw={600} py={10}>
          {t("workspace").transformDataset}
        </Menu.Label>
        <Grid gutter={4} w="100%" p={0} pb={4}>
          {Object.values(transformers)
            .filter((transformer) => transformer.id !== "unknown")
            .map((transformer) => (
              <Grid.Col key={transformer.id} span={4}>
                <SquareIconMenuButton
                  id={transformer.id}
                  parentId="transform"
                  tooltip={transformer.tooltip}
                  icon={transformer.icon}
                  label={transformer.label}
                  onClick={actionHandler}
                  disabled={!hasDataInput}
                />
              </Grid.Col>
            ))}
        </Grid>
      </Menu.Dropdown>
    </Menu>
  );

  return (
    <form onSubmit={form.onSubmit(handleSubmit)}>
      <Flex direction="column" gap={8} p="md">
        <Flex direction="column" gap={4}>
          <div className="transformer-item-wrapper">
            <WorkspaceSidebarFlowInput
              data={dataflowInput}
              isNewlyAdded={false}
              isRemoving={false}
            />
          </div>
        </Flex>

        <Flex direction="column" gap={8} mt="xs" pb={40}>
          <Text tt="uppercase" fz={12} lts={0.5} lh={1.4} fw={600} c="gray.9">
            {t("workspace").transformations}
          </Text>
          {transformerItems.length > 0 ? (
            <div className="transformers-container">
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
              >
                <SortableContext
                  items={sortableItems}
                  strategy={verticalListSortingStrategy}
                >
                  {transformerItems.map(({ transformer, actualIndex }) => {
                    return (
                      <div
                        key={actualIndex}
                        className="transformer-item-wrapper"
                      >
                        <WorkspaceSidebarFlowTransformer
                          transformer={transformer}
                          index={actualIndex}
                          onRemove={(index: number) =>
                            dataflowId && removeTransformer(dataflowId, index)
                          }
                          onUpdate={(index: number, transformer: Transformer) =>
                            dataflowId &&
                            updateTransformer(dataflowId, index, transformer)
                          }
                        />
                      </div>
                    );
                  })}
                </SortableContext>
              </DndContext>
              <AddTransformerMenuButton />
            </div>
          ) : (
            <Card p={0} shadow="0" withBorder>
              <Flex direction="column" pb={8}>
                <Flex justify="space-between" align="center">
                  <Flex p="sm" gap={8}>
                    <Group gap={8}>
                      <IconWashDrycleanOff
                        color="var(--mantine-color-gray-9)"
                        size={22}
                        stroke={1}
                      />
                      <Text fz={14} fw={500} c="gray.9">
                        {t("workspace").noTransformations}
                      </Text>
                    </Group>
                  </Flex>
                </Flex>
                <Flex px={8} gap={8}>
                  <AddTransformerMenuButton />
                </Flex>
              </Flex>
            </Card>
          )}
        </Flex>
      </Flex>
    </form>
  );
}
